<script lang="ts" setup generic="O = any, V = any">
import { computed, type VNode, provide } from 'vue';
import { useOptionsList, type OptionsListItem } from '../../composables';
import type { OptionsListProps } from '../../shared/types';
import UiSegmentedControlOption from './ui-segmented-control-option.vue';
import { SEGMENTED_CONTROL_CONTEXT } from './shared';

type Props = Omit<OptionsListProps<O, V>, 'asyncOptions' | 'hydrateValue' | 'optionsLimit'> & {
  modelValue?: V | null;
};

const { options = [], optionDisabled, optionLabel, optionValue, trackValueBy } = defineProps<Props>();

defineSlots<{
  default?: (options: OptionsListItem<O, V>[]) => VNode;
  option?: (option: OptionsListItem<O, V>) => VNode;
}>();

const modelValue = defineModel<V | null>({ default: null });

const { optionsList, selectOption, isValueSelected } = useOptionsList<O, V>({
  options: computed(() => options),
  optionDisabled,
  optionLabel,
  optionValue,
  trackValueBy,
  selectionMode: 'single',
  selected: modelValue,
  updateSelected(value) {
    modelValue.value = value as V | null;
  },
});

provide(SEGMENTED_CONTROL_CONTEXT, {
  isValueSelected,
  selectValue: (value: V) => {
    modelValue.value = value;
  },
});
</script>

<template>
  <div :class="$style.root">
    <slot v-bind="optionsList">
      <template v-for="option in optionsList" :key="option.label">
        <slot name="option" v-bind="option">
          <UiSegmentedControlOption :value="option.value" :disabled="option.disabled" @click="selectOption(option)">
            {{ option.label }}
          </UiSegmentedControlOption>
        </slot>
      </template>
    </slot>
  </div>
</template>

<style lang="scss" module>
.root {
  display: inline-flex;
  gap: 6px;
  padding: 2px;
  border-radius: 4px;
  background: rgba(35, 57, 93, 0.1);
}
</style>
